import { A } from '@osmosis/tailwind-ui/components/Text';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import React, { ReactNode, useEffect, useState } from 'react';
import {
  DenyReason,
  LockedSubProduct,
  useLearnAccess,
} from '../../../context/LearnAccessContext';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';

import { FormattedMessage, useIntl } from 'react-intl';
import { useUserContext } from '../../../context/UserContext';
import { SubProductState } from '@osmosis/shared/types/user';
import { UserProps } from '@osmosis/shared/types/user';
import { text0 } from '@osmosis/tailwind-ui/styles/text';

type Content = {
  heading: string | ReactNode;
  subheading: string | ReactNode;
};

const DEFAULT_CONTENT_KEY = 'default';
const EXPIRED_WITH_PACKAGE_KEY = 'expired_with_package';
const EXPIRED_KEY = 'expired';

const getExpiredKey = (
  user: UserProps,
  lockedSubProduct: LockedSubProduct | null,
) => {
  const userSubProducts = user?.subProducts || [];

  const activeSubProduct = userSubProducts.find(
    ({ state }) => state === SubProductState.active,
  );

  const activeProductKey =
    activeSubProduct?.groupName.replace(/ /g, '_').toLowerCase() || '';

  // learn is locked for user - show common expired for package
  if (lockedSubProduct) {
    return EXPIRED_WITH_PACKAGE_KEY;
  }
  // user doesn't have any active sub product - show expired depends on package existence
  if (!activeSubProduct) {
    return userSubProducts?.length ? EXPIRED_WITH_PACKAGE_KEY : EXPIRED_KEY;
  }

  // user has active sub product - show expired for that sub product
  return `${EXPIRED_KEY}_${activeProductKey}`;
};

/**
 * LockoutBlock component.
 */
const LockoutBlock = ({ isVisible }: { isVisible?: boolean }) => {
  const [content, setContent] = useState<Content | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const { lockedSubProduct, hasAccess, denyReason } = useLearnAccess();
  const { user, getPlansUrl } = useUserContext();

  const intl = useIntl();

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true); // Mount immediately
      setIsMounted(true); // Trigger fade-in
    } else {
      setIsMounted(false); // Start fade-out
      setTimeout(() => setShouldRender(false), 200); // Unmount after animation
    }
  }, [isVisible]); // Only runs when `isVisible` changes

  const textContent = {
    renew_button_text: intl.formatMessage({
      id: 'learn.lockout_block.button_text.renew',
      defaultMessage: 'Renew Now',
    }),
    see_plans_button_text: intl.formatMessage({
      id: 'learn.lockout_block.button_text.see_plans',
      defaultMessage: 'See Plans',
    }),
    expired_heading: intl.formatMessage({
      id: 'learn.lockout_block.heading.expired',
      defaultMessage: 'Uh-oh! It looks like your account has expired.',
    }),
    locked_heading: intl.formatMessage({
      id: 'learn.lockout_block.heading.locked',
      defaultMessage: "Sorry, you don't have access to this content.",
    }),
    default_subscribe_with_package: intl.formatMessage({
      id: 'learn.lockout_block.subheading.default_subscribe_with_package',
      defaultMessage:
        'Subscribe to the Osmosis Suite today to access thousands of videos, high-yield notes, and board-style questions.',
    }),
    default_subscribe: intl.formatMessage({
      id: 'learn.lockout_block.subheading.default_subscribe',
      defaultMessage:
        'Subscribe today to access thousands of videos, helpful notes, and so much more!',
    }),
    basic_sciences_subheading: intl.formatMessage({
      id: 'learn.lockout_block.subheading.basic_sciences',
      defaultMessage:
        'Subscribe to the Osmosis Suite today to access thousands of Basic Sciences videos, high-yield notes, and board-style questions.',
    }),

    clinical_sciences_subheading: intl.formatMessage({
      id: 'learn.lockout_block.subheading.clinical_sciences',
      defaultMessage:
        'Subscribe to the Osmosis Suite today to access hundreds of Clinical Sciences videos, decision-making trees, and board-style questions.',
    }),
  };

  const blockContent: { [key: string]: Content } = {
    // for logged out users
    loggedOut: {
      heading: intl.formatMessage({
        id: 'learn.lockout_block.heading.logged_out',
        defaultMessage: 'This content is only available for paid subscribers. ',
      }),
      subheading: intl.formatMessage({
        id: 'learn.lockout_block.subheading.logged_out',
        defaultMessage:
          'Sign in or subscribe to Osmosis today to access thousands of concise, visually engaging videos, helpful notes, board-style questions, and more!',
      }),
    },
    // expired product with sub-product which doesn't give access to the learn page
    [EXPIRED_WITH_PACKAGE_KEY]: {
      heading: textContent.expired_heading,
      subheading: textContent.default_subscribe_with_package,
    },
    // expired product without any sub-products
    [EXPIRED_KEY]: {
      heading: textContent.expired_heading,
      subheading: textContent.default_subscribe,
    },
    // expired product with Basic Sciences sub-product which gives access to the learn page
    expired_basic_sciences: {
      heading: textContent.expired_heading,
      subheading: textContent.basic_sciences_subheading,
    },
    // expired product with Clinical Sciences sub-product which gives access to the learn page
    expired_clinical_sciences: {
      heading: textContent.expired_heading,
      subheading: textContent.clinical_sciences_subheading,
    },
    // locked Basic Sciences
    basic_sciences: {
      heading: textContent.locked_heading,
      subheading: textContent.basic_sciences_subheading,
    },
    // locked Clinical Sciences
    clinical_sciences: {
      heading: textContent.locked_heading,
      subheading: textContent.clinical_sciences_subheading,
    },
    // default if any above doesn't match
    [DEFAULT_CONTENT_KEY]: {
      heading: textContent.locked_heading,
      subheading: textContent.default_subscribe,
    },
  };

  useEffect(() => {
    if (!user) {
      setContent(blockContent.loggedOut);

      return;
    }
    if (denyReason === DenyReason.expired) {
      const contentKey = getExpiredKey(user, lockedSubProduct);
      setContent(blockContent[contentKey] || blockContent[DEFAULT_CONTENT_KEY]);

      return;
    }
    if (lockedSubProduct?.id) {
      setContent(
        blockContent[lockedSubProduct.id] || blockContent[DEFAULT_CONTENT_KEY],
      );

      return;
    }
    setContent(blockContent[DEFAULT_CONTENT_KEY]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [denyReason, lockedSubProduct, user]);

  if (!shouldRender) return null; // Prevent rendering when fully unmounted

  return !hasAccess && content ? (
    <Flex
      className={`
        absolute
        left-0
        top-0
        z-20
        h-full
        w-full
        items-center
        justify-center
        rounded-[10px]
        bg-[rgba(0,0,0,.8)]
        transition-opacity
        duration-200
        ${isMounted ? 'opacity-100' : 'opacity-0'}
    `}
    >
      <Flex
        className={`flex-col gap-4 p-2 opacity-100 md:p-8 ${text0} items-center`}
      >
        <Icon icon={faLock} className={`text-5xl`} />
        <H2 className={`m-0 text-2xl font-bold ${text0} text-center`}>
          {content?.heading}
        </H2>
        <H3
          className={`text-center ${text0} mx-auto mb-[15px] mt-0 max-w-[530px] text-sm`}
        >
          {content?.subheading}
        </H3>
        <A
          href={getPlansUrl()}
          style={{
            textDecoration: 'none',
          }}
          className={``}
        >
          <Button
            size="m"
            data-testid={`view_plans_button`}
            className={`mb-2 rounded-[10rem] px-8`}
          >
            <FormattedMessage
              id="learn.lockout_modal.view_plans"
              defaultMessage="View Plans"
            />
          </Button>
        </A>
      </Flex>
    </Flex>
  ) : null;
};

export default LockoutBlock;
